import { PDFDocument } from "pdf-lib";
import { toArrayBuffer } from "./pdf-bytes";

const MAX_PAGE_SIDE_PT = 2000;

export type UploadKind = "pdf" | "jpeg" | "png";

function fileExtension(file: File): string {
  const dot = file.name.lastIndexOf(".");
  return dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : "";
}

/** File name without its extension, for use as a document name. */
export function fileBaseName(file: File): string {
  const dot = file.name.lastIndexOf(".");
  return dot > 0 ? file.name.slice(0, dot) : file.name;
}

/**
 * Classify a picked file from MIME type, falling back to the extension so a
 * JPEG with an empty type still converts.
 */
export function classifyUpload(file: File): UploadKind | null {
  const type = file.type.toLowerCase();
  const extension = fileExtension(file);

  if (type === "application/pdf" || extension === "pdf") return "pdf";
  if (type === "image/jpeg" || extension === "jpg" || extension === "jpeg") {
    return "jpeg";
  }
  if (type === "image/png" || extension === "png") return "png";
  return null;
}

function scaledPageSize(
  width: number,
  height: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= MAX_PAGE_SIDE_PT) {
    return { width, height };
  }
  const scale = MAX_PAGE_SIDE_PT / longest;
  return { width: width * scale, height: height * scale };
}

async function imageToPdfBuffer(
  bytes: ArrayBuffer,
  kind: "jpeg" | "png",
): Promise<ArrayBuffer> {
  const pdf = await PDFDocument.create();
  const image =
    kind === "jpeg"
      ? await pdf.embedJpg(bytes)
      : await pdf.embedPng(bytes);
  const { width, height } = scaledPageSize(image.width, image.height);
  const page = pdf.addPage([width, height]);
  page.drawImage(image, { x: 0, y: 0, width, height });
  return toArrayBuffer(await pdf.save());
}

/**
 * PDFs pass through. JPEG and PNG become a one-page PDF whose page matches
 * the image aspect ratio, scaled down when the longest side exceeds 2000pt.
 */
export async function fileToPdfBuffer(file: File): Promise<ArrayBuffer> {
  const kind = classifyUpload(file);
  if (!kind) {
    throw new Error(
      `Unsupported file type: ${file.name}. Use a PDF, JPEG, or PNG.`,
    );
  }
  const bytes = await file.arrayBuffer();
  if (kind === "pdf") return bytes;
  return imageToPdfBuffer(bytes, kind);
}
